/**
 * <input-datetime> — birth date and time, including the parts other chart
 * software buries.
 *
 * ◆ Three things live here rather than in a settings page, because they are
 * properties of *this birth* and not preferences:
 *
 *   • **the Julian/Gregorian calendar switch** — a date before 15 Oct 1582 is a
 *     Julian-calendar date, and reading it as Gregorian is ten days out
 *   • **the Vikrama / Śaka era** — a record may state either, and converting in
 *     your head is how a year goes astray by 57 or 78
 *   • **"time not known"** — a first-class answer, not a blank field
 *
 * ⚠ **"Time not known" must be easy to choose.** If the only way forward is to
 * type something into a time field, users type 12:00 or 06:00 and the chart
 * silently acquires a fabricated lagna. The checkbox exists so the honest answer
 * is the cheapest one.
 *
 * Emits `datetime-change` with `{ year, month, day, hour, minute, second,
 * calendar, era, timeKnown, ceYear, resolvedCalendar }`. It does **no** timezone
 * resolution — that needs the place, and <place-picker> owns it.
 *
 * ⚠ **The event is named `datetime-change`, not `change`, and that is load-bearing.**
 * The plain `change` events fired by the inner `<input>` and `<select>` elements
 * bubble out of this component, so a listener on the host cannot tell them from
 * the component's own. Worse, they arrive *after* it: the native event reaches
 * the inner handler first, which re-renders and dispatches the real event, and
 * only then does the original keep bubbling — so the page received a good detail
 * and then immediately an `undefined` one, and the last write won. The symptom
 * was a chart that silently never computed.
 *
 * ◆ A distinct name cannot be shadowed by a native one, and it makes the page
 * code say which event it means.
 *
 * © Manish Jagdish Thatte
 */

import { GREGORIAN_START_JD, julianDayFromCalendar } from '../astro/julian.js';

/**
 * Era offsets. A Vikrama year is 57 ahead of the Common Era and a Śaka year 78
 * behind it, at the new year; the month matters for an exact conversion, so
 * these are offered as a convenience with the resolved CE year shown back to the
 * user rather than applied silently.
 */
const ERAS = {
  ce: { label: 'Common Era (CE)', offset: 0 },
  vikrama: { label: 'Vikrama Saṁvat', offset: -57 },
  shaka: { label: 'Śaka', offset: 78 }
};

export class InputDateTime extends HTMLElement {
  connectedCallback() {
    if (this._built) return;
    this._built = true;
    this._state = {
      year: 2000, month: 1, day: 1, hour: 12, minute: 0, second: 0,
      calendar: 'auto', era: 'ce', timeKnown: true
    };
    this.render();
  }

  get value() { return { ...this._state, ...this.resolved() }; }

  /**
   * Load a birth moment into the fields (e.g. opening a saved chart).
   * Dispatches `datetime-change` so the page recomputes.
   */
  apply(fields = {}) {
    if (!this._built) this.connectedCallback();
    const next = { ...this._state };
    for (const key of ['year', 'month', 'day', 'hour', 'minute', 'second', 'calendar', 'era', 'timeKnown']) {
      if (fields[key] !== undefined) next[key] = fields[key];
    }
    this._state = next;
    this.render();
    this.dispatchEvent(new CustomEvent('datetime-change', {
      detail: this.value, bubbles: true
    }));
  }

  /** The CE year and the calendar actually in force, after era and auto-switch. */
  resolved() {
    const { year, era, month, day, calendar } = this._state;
    const ceYear = year + (ERAS[era]?.offset ?? 0);
    let cal = calendar;
    if (cal === 'auto') {
      const jd = julianDayFromCalendar(ceYear, month, day);
      cal = jd >= GREGORIAN_START_JD ? 'gregorian' : 'julian';
    }
    return { ceYear, resolvedCalendar: cal };
  }

  render() {
    // ⚠ Element ids are per document. A page that shows two of these (Guṇa Milan)
    // sets `data-id-prefix` on each so their labels point at their own fields.
    idPrefix = this.dataset.idPrefix ?? '';
    this.textContent = '';
    const s = this._state;
    const { ceYear, resolvedCalendar } = this.resolved();

    const fs = document.createElement('fieldset');
    fs.className = 'field-group';
    const legend = document.createElement('legend');
    legend.textContent = 'Birth date and time';
    fs.append(legend);

    const era = select('Era', 'era', Object.entries(ERAS).map(([v, e]) => [v, e.label]), s.era,
      (v) => this.update({ era: v }));
    fs.append(era);

    fs.append(number('Year', 'year', s.year, { min: -4000, max: 4000 },
      (v) => this.update({ year: v })));
    fs.append(number('Month', 'month', s.month, { min: 1, max: 12 },
      (v) => this.update({ month: v })));
    fs.append(number('Day', 'day', s.day, { min: 1, max: 31 },
      (v) => this.update({ day: v })));

    // ⚠ Shown whenever the era is not CE, because a user entering Vikrama 1926
    // must be able to see that this is 1869 CE before they trust the chart.
    if (s.era !== 'ce') {
      fs.append(note(`Vikrama/Śaka years shift at their own new year, so this is ` +
        `approximately ${ceYear} CE. Check the month if the birth is near the turn of the year.`));
    }

    const timeKnown = checkbox('The birth time is known', 'timeKnown', s.timeKnown,
      (v) => this.update({ timeKnown: v }));
    fs.append(timeKnown);

    if (s.timeKnown) {
      fs.append(number('Hour', 'hour', s.hour, { min: 0, max: 23 },
        (v) => this.update({ hour: v })));
      fs.append(number('Minute', 'minute', s.minute, { min: 0, max: 59 },
        (v) => this.update({ minute: v })));
      fs.append(number('Second', 'second', s.second, { min: 0, max: 59 },
        (v) => this.update({ second: v })));
      fs.append(note('Enter the time as the clock read it at the place of birth. ' +
        'The correct UTC offset for that date is looked up, not assumed.'));
    } else {
      fs.append(note('Without a birth time the chart will show every graha and no bhāvas, ' +
        'and no lagna will be guessed. The Moon moves about 13° a day, so its rāśi may ' +
        'also be uncertain — that is shown on the chart.'));
    }

    fs.append(select('Calendar', 'calendar', [
      ['auto', `Automatic (${resolvedCalendar} for this date)`],
      ['gregorian', 'Gregorian'],
      ['julian', 'Julian']
    ], s.calendar, (v) => this.update({ calendar: v })));

    if (resolvedCalendar === 'julian') {
      fs.append(note('This date falls before the Gregorian reform of 15 October 1582, ' +
        'so it is read in the Julian calendar. Reading it as Gregorian would be ten days out.'));
    }

    this.append(fs);
  }

  update(patch) {
    this._state = { ...this._state, ...patch };
    this.render();
    this.dispatchEvent(new CustomEvent('datetime-change', {
      detail: this.value, bubbles: true
    }));
  }
}

/* ── Small field builders. Plain labels and inputs; no framework. ─────────── */

/** Set by render() for the instance being built; helpers below read it. */
let idPrefix = '';

function field(labelText, id) {
  const wrap = document.createElement('div');
  wrap.className = 'field';
  const label = document.createElement('label');
  label.setAttribute('for', `${idPrefix}dt-${id}`);
  label.textContent = labelText;
  wrap.append(label);
  return wrap;
}

function number(labelText, id, value, { min, max }, onChange) {
  const wrap = field(labelText, id);
  const input = document.createElement('input');
  input.type = 'number';
  input.id = `${idPrefix}dt-${id}`;
  input.value = String(value);
  input.min = String(min);
  input.max = String(max);
  input.inputMode = 'numeric';
  input.addEventListener('change', () => {
    const v = Number(input.value);
    if (Number.isFinite(v)) onChange(v);
  });
  wrap.append(input);
  return wrap;
}

function select(labelText, id, options, value, onChange) {
  const wrap = field(labelText, id);
  const sel = document.createElement('select');
  sel.id = `${idPrefix}dt-${id}`;
  for (const [v, label] of options) {
    const opt = document.createElement('option');
    opt.value = v;
    opt.textContent = label;
    if (v === value) opt.selected = true;
    sel.append(opt);
  }
  sel.addEventListener('change', () => onChange(sel.value));
  wrap.append(sel);
  return wrap;
}

function checkbox(labelText, id, checked, onChange) {
  const wrap = document.createElement('div');
  wrap.className = 'field field--check';
  const input = document.createElement('input');
  input.type = 'checkbox';
  input.id = `${idPrefix}dt-${id}`;
  input.checked = Boolean(checked);
  input.addEventListener('change', () => onChange(input.checked));
  const label = document.createElement('label');
  label.setAttribute('for', `${idPrefix}dt-${id}`);
  label.textContent = labelText;
  wrap.append(input, label);
  return wrap;
}

function note(text) {
  const p = document.createElement('p');
  p.className = 'field-note';
  p.textContent = text;
  return p;
}

customElements.define('input-datetime', InputDateTime);
